import { PropsWithChildren, ReactNode } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { token } from "@atlaskit/tokens";

import { Picture, PictureProps } from "@/components/wpl/image/src";
import { wacToken } from "@/libraries/design/foundations";

import { Link, LinkProps } from "../../link/src/link";

const iconSizes = {
    "x-small": 24,
    "small": 32,
    "medium": 48,
    "large": 64
};

export interface CardProps extends PropsWithChildren {
    eyebrow?: string;
    title?: string | React.ReactElement<typeof FormattedMessage>;
    titleAs?: "h2" | "h3" | "h4" | "h5" | "p";
    body?: string | ReactNode;
    callToAction?: LinkProps;
    image?: PictureProps;
    icon?: {
        linkedIcon?: {
            url: string;
            size?: keyof typeof iconSizes;
        };
        iconComponent?: JSX.Element;
    };
    align?: "start" | "center";
    containerType?: keyof JSX.IntrinsicElements;
}

// eslint-disable-next-line complexity
export const Card = ({
    eyebrow,
    title,
    body,
    image,
    icon,
    callToAction,
    align = "start",
    ...props
}: CardProps) => {
    const { titleAs: TitleComponent = "h3", containerType: Container = "div" } =
        props;
    const isLinkableCard = !!callToAction;

    const linkedIconSize = icon?.linkedIcon?.size
        ? iconSizes[icon.linkedIcon.size]
        : iconSizes["medium"];

    const titleId = title?.toString().replace(/\s+/g, "") ?? "title";
    const callToActionId =
        callToAction?.text.replace(/\s+/g, "") ?? "call-to-action";

    return (
        <Container
            css={[cardContainer, isLinkableCard && linkableCardContainer]}
        >
            {image && (
                <div css={cardImageContainer}>
                    <Picture {...image} css={imageStyle} />
                </div>
            )}
            <div
                css={[
                    cardBodyContainer,
                    align === "center" && centeredCardContainer
                ]}
            >
                {eyebrow && (
                    <p className="eyebrow-md" css={resetMargin}>
                        {eyebrow}
                    </p>
                )}
                {icon?.linkedIcon && (
                    <div css={iconContainer}>
                        <Picture
                            image={{
                                src: icon.linkedIcon.url,
                                alt: "",
                                width: linkedIconSize,
                                height: linkedIconSize
                            }}
                        />
                    </div>
                )}
                {icon?.iconComponent && (
                    <div css={iconContainer}>{icon.iconComponent}</div>
                )}
                {title && (
                    <TitleComponent
                        className="card-title"
                        id={titleId}
                        css={resetMargin}
                    >
                        {title}
                    </TitleComponent>
                )}
                {body && <p css={resetMargin}>{body}</p>}
                {isLinkableCard && (
                    <div css={linkContainer}>
                        <Link
                            id={callToActionId}
                            ariaLabelledby={`${callToActionId} ${titleId}`}
                            type="primary"
                            {...callToAction}
                            className={`full-card ${
                                callToAction?.className ?? ""
                            }`}
                        />
                    </div>
                )}
            </div>
        </Container>
    );
};

const cardContainer = css({
    borderRadius: wacToken("corner.radius.card"),
    background: wacToken("color.neutral.light0"),
    border: `1px solid ${wacToken("color.neutral.light400")}`,
    display: "flex",
    flexDirection: "column"
});
const centeredCardContainer = css({
    alignItems: "center",
    textAlign: "center"
});
const linkableCardContainer = css({
    position: "relative",
    transition: "box-shadow 125ms ease-in",
    // Need to style anchors differently for a linkable card and cannot pass in CompiledCSS
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [".full-card"]: {
        "::before": {
            content: "",
            inset: "0",
            position: "absolute",
            zIndex: 1
        }
    },
    ["&:has(.full-card:hover), &:has(.full-card:focus)"]: {
        transition: "box-shadow 125ms ease-out",
        boxShadow: token("elevation.shadow.overlay")
    }
});
const cardImageContainer = css({
    overflow: "clip",
    borderStartStartRadius: wacToken("corner.radius.card"),
    borderStartEndRadius: wacToken("corner.radius.card")
});
const imageStyle = css({
    // Issue in Picture component applies styles to picture instead of img
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    img: {
        aspectRatio: "16 / 9",
        width: "100%",
        height: "auto",
        objectFit: "contain"
    }
});
const cardBodyContainer = css({
    padding: token("space.300"),
    flex: "1",
    display: "flex",
    flexDirection: "column",
    gap: token("space.100")
});
const resetMargin = css({
    margin: "0 !important"
});
const iconContainer = css({
    width: "60px",
    height: "60px",
    display: "grid",
    placeItems: "center",
    marginBlockEnd: token("space.200")
});
const linkContainer = css({
    marginBlockStart: "auto",
    paddingBlockStart: token("space.200")
});
